import { HttpClient, HttpParams } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { UntypedFormArray } from "@angular/forms";
import {
  Contact,
  Document,
  FlightAvailReq,
  FlightAvailRes,
  PricingRes,
  SeatsAvailabilityRes,
  StandAloneCatalogueRes,
  TitleType,
  Traveller,
  TravellerType,
  DocumentType,
} from "@core/models";
import { environment } from "@env/environment";
import { from, Observable, take } from "rxjs";
import { TokenService } from "./token.service";
import moment from "moment";
import { TravelUtilsService } from "./travel-utils.service";
import municipalitiesJson from "../../../../assets/utils/resident-municipalities.json";
import { StateService } from "./state.service";
import { LetItGoType } from "@core/models/letitgo-type";
import { TranslateService } from "@ngx-translate/core";
import { UtilsService } from "./utils.service";
import swal from "sweetalert2";
import { Router } from "@angular/router";
import { NgxToastrService } from "./ngx.toastr.service";
import { BookedService } from "./booked.service";
import { AuthService } from "./auth.service";

@Injectable({
  providedIn: "root",
})
export class FlightLleegoService {
  travelerForm: UntypedFormArray;
  munipalitiesList = municipalitiesJson;
  passengerBookingParams: any = null;

  constructor(private http: HttpClient,
    private tokenService: TokenService,
    private travelUtilsService: TravelUtilsService,
    private stateService: StateService,
    public translate: TranslateService,
    public utilsService: UtilsService,
    private router: Router,
    private ngxToastrService: NgxToastrService,
    private bookedService: BookedService, 
    private authService: AuthService, 
  ) {}

  getTravellerType(birthDate: any): TravellerType {
    const travellerAge = moment().diff(
      moment(
        `${birthDate.year}-${birthDate.month}-${birthDate.day}`,
        "YYYY-MM-DD"
      ),
      "years"
    );
    if (travellerAge >= 12) {
      return TravellerType.ADT;
    } else if (travellerAge < 12 && travellerAge > 2) {
      return TravellerType.CHD;
    } else {
      return TravellerType.INF;
    }
  }

  getAirportName(iataCode: string, flightSelectedStateBackup): string {
    const airportData = flightSelectedStateBackup.data.port[iataCode];
    return airportData ? airportData.name : iataCode;
  }

  createHolderContact(travelerForm, user): Contact {
      /* const holderContactPhone = {
        country_pref: "34",
        number: "648200516",
      }; */
  
      const contact: Contact = {
        type: "Passenger",
        /* phones: [holderContactPhone], */
        country: travelerForm.nationality.code,
        address: user.address ? user.address : '',
      };
      if (travelerForm.city && travelerForm.city.trim() !== '') {
        contact.city = travelerForm.city;
        contact.postal_code = this.munipalitiesList.find((c) => c.municipality === travelerForm.city)?.code;
      }
      return contact;
    }

  getDocumentType(formType: string): DocumentType {
    switch (formType) {
      case "IDENTITY_CARD":
        return DocumentType.NI;
      case "PASSPORT":
        return DocumentType.PP;
      case "NIE":
        return DocumentType.NIE;
      default:
        break;
    }
  }

  getVyooPrice(
    farePricing,
    totalPrice,
    canUseResident,
    selectedBaggages: any[] = [],
    selectedPriority: any[] = []
  ) {
    let total = farePricing ? parseFloat(farePricing.total) : Number(totalPrice || 0);

    if (canUseResident && this.stateService.totalPrice) {
      total = Number(this.stateService.totalPrice || total);
    }

    for (const baggage of selectedBaggages || []) {
      const paxCount =
        Array.isArray(baggage?.pax_index) ? baggage.pax_index.length :
        Array.isArray(baggage?.pax_references) ? baggage.pax_references.length :
        1;

      total += Number(baggage?.total || 0) * paxCount;
    }

    for (const p of selectedPriority || []) {
      const paxCount =
        Array.isArray(p?.pax_index) ? p.pax_index.length :
        Array.isArray(p?.pax_references) ? p.pax_references.length :
        1;

      total += Number(p?.total || 0) * paxCount;
    }

    return total;
  }


  private normalizePassengerFieldName(name: string): string {
    return (name || "").toLowerCase().trim();
  }

  private getPassengerFieldAliases(): Record<string, string[]> {
    return {
      name: ["name"],
      surname: ["surname", "lastname", "last_name"],
      lastsurname: ["surname", "lastname", "last_name"],
      gender: ["gender", "sex"],
      email: ["email", "mail"],
      phonenumber: ["phone", "phone_number", "mobile", "contact_phone"],
      birthdate: ["birthdate", "birth_date", "date_of_birth"],
      expirydate: [
        "expirydate",
        "expiry_date",
        "document_expiry",
        "doc_expiry",
        "doc_expiration_date"
      ],
      issuancedate: [
        "issuancedate",
        "issuance_date",
        "document_issuance",
        "doc_issuance",
        "doc_expedition_date"
      ],
      documenttype: ["doc_type", "document_type", "doctype"],
      documentnumber: ["doc_number", "document_number", "document"],
      expecountry: [
        "issuance_country",
        "document_country",
        "doc_country",
        "expedition_country",
        "doc_expedition_country"
      ],
      nationality: ["nationality"],
      city: ["city", "residence_city"],
      documents: ["frequent_traveller", "ff_card", "ff", "documents"]
    };
  }

  private getPassengerFields(): any[] {
    return Array.isArray(this.passengerBookingParams?.fields)
      ? this.passengerBookingParams.fields
      : [];
  }

  private hasPassengerField(fieldName: string): boolean {
    const normalized = this.normalizePassengerFieldName(fieldName);
    const aliases = this.getPassengerFieldAliases();
    const acceptedNames = aliases[normalized] || [normalized];

    const fields = this.getPassengerFields();
    if (!fields.length) return true;

    return fields.some(field =>
      acceptedNames.includes(this.normalizePassengerFieldName(field?.name))
    );
  }

  createTravellersData(user): Traveller[] {
    // const responsibles: PersonInCharge = { name: 'Vyootrip', address: 'C/ Leonardo da Vinci, 18', phone: '648200516' }
    const travellerData: Traveller[] = [];
    this.travelerForm.value.forEach((travelerForm) => {
      const contact: Contact = this.createHolderContact(travelerForm, user);
      const travelerDocumentsArr = [];

      const selectedCardIndex = travelerForm.documents?.selectedFfCard;
      if (selectedCardIndex != null && travelerForm.documents.ffCard[selectedCardIndex]) {
        const selectedFfCard = travelerForm.documents.ffCard[selectedCardIndex];

        travelerDocumentsArr.push({
          type: travelerForm.documents.documentType,
          number: selectedFfCard.cardNumber,
        });
      }

      const needsMainDocument =
        this.hasPassengerField("documentType") ||
        this.hasPassengerField("documentNumber") ||
        this.hasPassengerField("nationality") ||
        this.hasPassengerField("expeCountry") ||
        this.hasPassengerField("expiryDate") ||
        this.hasPassengerField("issuanceDate") ||
        this.hasPassengerField("city");

      if (needsMainDocument) {
        const travellerDocument: Document = {} as Document;

        if (this.hasPassengerField("documentType") && travelerForm.documentType) {
          travellerDocument.type = this.getDocumentType(travelerForm.documentType);
        }

        if (this.hasPassengerField("documentNumber") && travelerForm.documentNumber) {
          travellerDocument.number = travelerForm.documentNumber;
        }

        if (this.hasPassengerField("nationality") && travelerForm.nationality?.code) {
          travellerDocument.nationality = travelerForm.nationality.code;
          travellerDocument.country = travelerForm.nationality.code;
        }

        if (
          this.hasPassengerField("city") &&
          travellerDocument.type?.toUpperCase() === "NI" &&
          travelerForm.city
        ) {
          travellerDocument["city"] = travelerForm.city;
        }

        if (
          this.hasPassengerField("expiryDate") &&
          travelerForm.expiryDate != null &&
          travelerForm.expiryDate !== ""
        ) {
          travellerDocument.expiry_date = moment([
            travelerForm.expiryDate.year,
            travelerForm.expiryDate.month - 1,
            travelerForm.expiryDate.day,
          ]).format("YYYY-MM-DD");
        }

        if (
          this.hasPassengerField("issuanceDate") &&
          travelerForm.issuanceDate != null &&
          travelerForm.issuanceDate !== ""
        ) {
          travellerDocument.issue_date = moment([
            travelerForm.issuanceDate.year,
            travelerForm.issuanceDate.month - 1,
            travelerForm.issuanceDate.day,
          ]).format("YYYY-MM-DD");
        }

        travelerDocumentsArr.push(travellerDocument);
      }

      const traveller: Traveller = {
        uuid: travelerForm.uuid,
        documents: travelerDocumentsArr,
        contact: contact,
      };

      if (this.hasPassengerField("birthDate") && travelerForm.birthDate) {
        traveller.type = this.getTravellerType(travelerForm.birthDate);
        traveller.birth_date = `${travelerForm.birthDate.year}-${travelerForm.birthDate.month}-${travelerForm.birthDate.day}`;
      } else {
        traveller.type = "ADT";
      }

      if (this.hasPassengerField("gender")) {
        traveller.title =
          travelerForm.gender?.toLowerCase() === "male"
            ? TitleType.Mr
            : TitleType.Ms;
      }

      if (this.hasPassengerField("name")) {
        traveller.name = travelerForm.name;
      }

      if (this.hasPassengerField("surname")) {
        traveller.surnames = [travelerForm.surname];
        if (travelerForm.lastSurname != null && travelerForm.lastSurname !== "") {
          traveller.surnames.push(travelerForm.lastSurname);
        }
      }
      travellerData.push(traveller);
    });
    return travellerData;
  }

  letItGo(
      user: any,
      selectedFlights,
      travelersAge,
      flightSelectedStateBackup,
      farePricing,
      totalPrice,
      canUseResident,
      selectedBaggages,
      selectedPriority,
      extraInfoForm,
      fareName,
      fareSelected,
      residentOptions,
      projectData,
      userUuid,
      externalUser,
      canUseTRM,
      navigationExtras,
      lleegoBookingID?: string,
      locator?: string,
      paymentId?: number,
      ancillaries?: any,
      insurance?: any,
      unavail?: boolean,
      priceChanged?,
      expectedAmountSeats?,
      selectedSeats?,
      skipRedirect?: boolean,
    ) {
      const parsedAncillaries: Array<{ journey?: 'ida' | 'vuelta'; price: number; name: string }> = [];
      const parsedPriority: Array<{
        journey?: 'ida' | 'vuelta';
        unitPrice: number;
        paxCount: number;
        totalPrice: number;
        name: string;
        paxRefs?: any[];
      }> = [];
      const travellerData: Traveller[] = this.createTravellersData(user);
      this.travelUtilsService.removeTravelLocalStorage();
  
      const calculateStopDurations = (flights: any[]) => {
        const stopDurations = [];
        for (let i = 0; i < flights.length - 1; i++) {
          const currentArrival = moment(flights[i].object.arrival_date);
          const nextDeparture = moment(flights[i + 1].object.departure_date);
          const duration = moment.duration(nextDeparture.diff(currentArrival));
          stopDurations.push({
            arrival: flights[i].object.arrival,
            departure: flights[i + 1].object.departure,
            duration: `${duration.hours()}h ${duration.minutes()}m`,
            durationInMinutes: duration.asMinutes()
          });
        }
        return stopDurations;
      };
  
      const goingStopsDuration = calculateStopDurations(selectedFlights[0]);
  
      let returnStopsDuration = [];
      if (selectedFlights.length > 1) {
        returnStopsDuration = calculateStopDurations(selectedFlights[1]);
      }
  
      let letItGoTravel;
      if (
        this.stateService.flightTravelers &&
        this.stateService.flightTravelers.length > 0
      ) {
        letItGoTravel =
          this.stateService.flightTravelers.length > travelersAge.length
            ? this.stateService.flightTravelers.length
            : travelersAge.length;
      } else {
        letItGoTravel = travelersAge.length;
      }
      const getJourney = (item: any): 'ida' | 'vuelta' => {
      const isOutward =
        Array.isArray(item.seg_references) &&
        item.seg_references.every((v, i) => v === selectedFlights[0][i]?.id);
        return selectedFlights.length > 1 ? (isOutward ? 'ida' : 'vuelta') : 'ida';
      };

      if (Array.isArray(ancillaries) && ancillaries.length > 0) {
        ancillaries.forEach((a) => {
          const journey = getJourney(a);

          if (a?.category === 'PriorityBoard') {
            const unitPrice = Number(a.total ?? a.price ?? 0);
            const paxCount =
              Array.isArray(a?.pax_index) ? a.pax_index.length :
              Array.isArray(a?.pax_references) ? a.pax_references.length :
              1;

            parsedPriority.push({
              journey,
              unitPrice,
              paxCount,
              totalPrice: unitPrice * paxCount,
              name: a.short_name || a.name || 'Priority',
              paxRefs: a.pax_references ?? [],
            });
            return;
          }

          parsedAncillaries.push({
            journey,
            price: Number(a.total ?? a.price ?? 0),
            name: a.short_name || a.name || 'Ancillary',
          });
        });
      }
      const goingStops = selectedFlights[0].slice(
        1,
        selectedFlights[0].length
      );
      let goingText = "Escala Ida:";
      if (goingStops.length > 1) {
        goingText = "Escalas Ida:";
      }
      const author = [externalUser?.name, externalUser?.lastname]
        .filter(Boolean)
        .join(" ")
        .trim();
      const body = {
        itinerary: `Ida: (${selectedFlights[0][0].airline.name
          }) ${this.getAirportName(selectedFlights[0][0].object.departure, flightSelectedStateBackup)}  - ${this.getAirportName(selectedFlights[0][selectedFlights[0].length - 1].object.arrival, flightSelectedStateBackup)}`,
        travellers: letItGoTravel,
        dates: moment(selectedFlights[0][0].object.departure_date).format(
          "DD/MM/YYYY HH:mm"
        ),
        price: priceChanged
                ? priceChanged
                : this.getVyooPrice(farePricing, totalPrice, canUseResident, selectedBaggages, selectedPriority) + (expectedAmountSeats ? expectedAmountSeats : 0),
        stops: null,
        seats: selectedSeats?? [],
        lleegoBookingID,
        locator,
        baggages: parsedAncillaries,
        priority: parsedPriority,
        insurance: insurance,
        stopsText: null,
        goingStopsDuration: goingStopsDuration,
        returnStopsDuration: returnStopsDuration,
        travelerList: travellerData,
        unavail: unavail,
        author: author || "No consta",
      };
  
      if (goingStops.length > 0) {
        body.stopsText = `${goingText} ${goingStops
          .map((seg, index) => {
            const arrivalTime = moment(selectedFlights[0][index].object.arrival_date).format("HH:mm");
            const departureTime = moment(selectedFlights[0][index + 1].object.departure_date).format("HH:mm");
            const arrivalCity = selectedFlights[0][index].object.arrival;
            const nextArrivalCity = selectedFlights[0][index + 1]?.object.arrival;
            return `${this.getAirportName(seg.object.departure, flightSelectedStateBackup)}-${this.getAirportName(seg.object.arrival, flightSelectedStateBackup)} (Aerolínea: ${seg.airline.name}) (IATA aerolinea: ${seg.object.marketing_company}) duración: ${goingStopsDuration[index]?.duration || "N/A"}, llega a ${arrivalCity} a las ${arrivalTime}, sale hacia ${nextArrivalCity} a las ${departureTime}`;
          })
          .join(", ")}`;
      } else {
        body.stopsText = `${goingText} ninguna`;
      }
      body.stops = goingStops.length;
      const extra = extraInfoForm?.value ?? extraInfoForm ?? {};
      body["customCode"] = extra.customCode || "";
      body["costCenter"] = extra.costCenter || "";
      body["annotationsExtras"] = extra.annotationsExtras || "";
  
      if (fareName && fareSelected) {
        body["fares"] = { fareName: fareName, fareConditions: fareSelected.services ?? [], price: fareSelected.total_price }
      }
  
      if (selectedFlights.length > 1) {
        body.dates =
          moment(selectedFlights[0][0].object.departure_date).format(
            "DD/MM/YYYY HH:mm"
          ) +
          " - " +
          moment(
            selectedFlights[selectedFlights.length - 1][0].object.departure_date
          ).format("DD/MM/YYYY HH:mm");
        const returnStops = selectedFlights[
          selectedFlights.length - 1
        ].slice(1, selectedFlights[selectedFlights.length - 1].length);
        let returnText = "Escala Vuelta:";
        if (returnStops.length > 1) {
          returnText = "Escalas Vuelta:";
        }
        body.itinerary += `\nVuelta: (IATA aerolinea: ${selectedFlights[selectedFlights.length - 1][0].object
          .marketing_company
          }) (${selectedFlights[selectedFlights.length - 1][0].airline.name
          }) ${this.getAirportName(selectedFlights[selectedFlights.length - 1][0].object.departure, flightSelectedStateBackup)} - ${this.getAirportName(
            selectedFlights[selectedFlights.length - 1][selectedFlights[selectedFlights.length - 1].length - 1].object.arrival, flightSelectedStateBackup)}`;
  
        if (returnStops.length > 0) {
          body.stopsText += `\n${returnText} ${returnStops
            .map((seg, index) => {
              const arrivalTime = moment(selectedFlights[1][index].object.arrival_date).format("HH:mm");
              const departureTime = moment(selectedFlights[1][index + 1]?.object.departure_date).format("HH:mm");
              const arrivalCity = selectedFlights[1][index].object.arrival;
              const nextArrivalCity = selectedFlights[1][index + 1]?.object.arrival;
              return `${this.getAirportName(seg.object.departure, flightSelectedStateBackup)}-${this.getAirportName(seg.object.arrival, flightSelectedStateBackup)} (Aerolínea: ${seg.airline.name}) (IATA aerolinea: ${seg.object.marketing_company}) duración: ${returnStopsDuration[index]?.duration || "N/A"}, llega a ${arrivalCity} a las ${arrivalTime}, sale hacia ${nextArrivalCity} a las ${departureTime}`;
            })
            .join(", ")}`;
        } else {
          body.stopsText += `\n${returnText} ninguna`;
        }
        body.stops += returnStops.length;
      }
  
      if (canUseResident) {
        residentOptions.find((r) => {
          if (r.value === canUseResident) {
            body["canUseResident"] = r.text;
          }
        })
      }
  
      if (projectData && projectData.selectedProject && projectData.selectedProject.uuid !== "none") {
        body["project"] = projectData.selectedProject.title || "";
      }
  
      this.utilsService.sendEmailOperations(
        LetItGoType.flight,
        body,
        userUuid ? (Array.isArray(userUuid) ? userUuid[0] : userUuid) : null
      );
      this.translate
        .get(["common.congrat", "common.operations"])
        .subscribe((response) => {
          swal
            .fire({
              position: "center",
              icon: "success",
              title: response["common.congrat"],
              text: response["common.operations"],
              showConfirmButton: false,
              timer: this.utilsService.letItGoTimer,
              timerProgressBar: true,
            })
            .then(() => {
              if (!skipRedirect) {
                this.redirectToInit(externalUser, canUseTRM, navigationExtras);
              } else {
                this.utilsService.loadingFlights = false;
              }
            });
        });
    }

  redirectToInit(externalUser, canUseTRM, navigationExtras) {
    this.utilsService.loadingFlights = false;
    const route = this.utilsService.getNavigationRoute(externalUser, null, canUseTRM);
    if (route) {
      this.router.navigate([route], { ...navigationExtras, queryParams: { specialAction: true } });
    }
  }

  addToTravel(flight: any, projectData) {
    if (this.utilsService.hasProcessedUuid(flight.uuid)) {
      return;
    }
    this.utilsService.addProcessedUuid(flight.uuid);

    const checkInAux = moment(flight.departDate).format("YYYY-MM-DD");
    const addBody: any = {
      item: {
        type: "flight",
        uuid: flight.uuid,
        initDate: checkInAux,
      },
    };
    if (flight.returnDate != null) {
      const checkOutAux = moment(flight.returnDate).format("YYYY-MM-DD");
      addBody.item.endDate = checkOutAux;
    }
    this.travelUtilsService.addItemToTravel(addBody, projectData.selectedProject.uuid);
  }

  addBookingToTravel(item: { type:'flight'|'booking', uuid:string, initDate:string, endDate?:string }, projectData) {
    if (this.utilsService.hasProcessedUuid(item.uuid)) return;
    this.utilsService.addProcessedUuid(item.uuid);

    this.travelUtilsService.addItemToTravel({ item }, projectData.selectedProject.uuid);
  }

  openSeatSelectionDialog(
    user: any,
    selectedFlights,
    travelersAge,
    flightSelectedStateBackup,
    farePricing,
    totalPrice,
    canUseResident,
    selectedBaggages,
    selectedPriority,
    extraInfoForm,
    fareName,
    fareSelected,
    residentOptions,
    projectData,
    userUuid,
    externalUser,
    canUseTRM,
    navigationExtras,
    lleegoBookingID?: string,
    locator?: string,
    paymentId?: number,
    expected_amount?: number,
    ancillaries?: any
  ) {
    this.utilsService.loadingFlights = true;
    if (lleegoBookingID == null) {
      this.letItGo(user,
      selectedFlights,
      travelersAge,
      flightSelectedStateBackup,
      farePricing,
      totalPrice,
      canUseResident,
      selectedBaggages,
      selectedPriority,
      extraInfoForm,
      fareName,
      fareSelected,
      residentOptions,
      projectData,
      userUuid,
      externalUser,
      canUseTRM,
      navigationExtras, lleegoBookingID, locator, paymentId, ancillaries);
    } else {
      this.redirectToInit(externalUser, canUseTRM, navigationExtras);
    }
  }

  doFlightBooking(confirmDto, projectData, bookingQuery, fareSelected, expectedAmountSeats, totalBaggagePrice, priorityPrice,
    priceChanged, navigationExtras, externalUser, canUseTRM, user, selectedFlights, travelersAge, flightSelectedStateBackup, farePricing, totalPrice,
    canUseResident, selectedBaggages, selectedPriority, extraInfoForm, fareName, residentOptions, userUuid, isPackageRate,
    callbacks?: {
      onSuccess?: (flightResult: any) => void;
      onAvailOut?: () => void;
      onFail?: (reason: 'AVAIL_OUT'|'OUTDATED_817'|'CARD_INVALID'|'LET_IT_GO'|'CANCELLED_PRICE_CHANGE'|'OTHER', err?: any) => void;
      onLetItGo?: (err: any) => void;
    }) {
    const isAccommodationPack = isPackageRate === 'accommodation';
    const isFlightPack = isPackageRate === 'flight';
    this.postFlightBooking(confirmDto).subscribe(
      (flightResult) => {
        callbacks?.onSuccess?.(flightResult);
        this.utilsService.loadingFlights = false;
        if (!isPackageRate){
        this.translate
          .get(["common.congrat", "flight.success"])
          .subscribe((response) => {
            swal
              .fire({
                position: "center",
                icon: "success",
                title: response["common.congrat"],
                text: response["flight.success"],
                showConfirmButton: false,
                timer: 3000,
              })
              .then(() => {
                if(projectData && projectData.selectedProject && projectData.selectedProject.uuid !== "none"){
                  if (flightResult.createResponse) {
                    this.addToTravel(flightResult.createResponse, projectData);
                  }
                  if (flightResult.additionalBookings && Array.isArray(flightResult.additionalBookings)) {
                    flightResult.additionalBookings.forEach((booking: any) => {
                      this.addToTravel(booking, projectData);
                    });
                  }
                }

                this.cleanData();
                
                this.openSeatSelectionDialog(
                  user,
                  selectedFlights,
                  travelersAge,
                  flightSelectedStateBackup,
                  farePricing,
                  totalPrice,
                  canUseResident,
                  selectedBaggages,
                  selectedPriority,
                  extraInfoForm,
                  fareName,
                  fareSelected,
                  residentOptions,
                  projectData,
                  userUuid,
                  externalUser,
                  canUseTRM,
                  navigationExtras,
                  flightResult.createResponse.lleegoReservationUuid,
                  flightResult.createResponse.pnr,
                  flightResult.createResponse.lleegoPayment,
                  flightResult.createResponse.price,
                  bookingQuery.ancillaries
                );
              });
          });
        } else {
          if(projectData && projectData.selectedProject && projectData.selectedProject.uuid !== "none"){
            if (flightResult.createResponse) {
              this.addToTravel(flightResult.createResponse, projectData);
            }
            if (flightResult.additionalBookings && Array.isArray(flightResult.additionalBookings)) {
              flightResult.additionalBookings.forEach((booking: any) => {
                this.addToTravel(booking, projectData);
              });
            }
          }
        }
      },
      (err) => {
        if (err.status === 409 && err.error.error === "Price changed") {
          const currentAmount = err.error.currentAmount.toFixed(2);
          const previousAmount = err.error.originalAmount.toFixed(2);
          const currency = err.error.currency;
          const previousPrice = (previousAmount*1 + fareSelected.vyooCommission).toFixed(2);

          fareSelected.total = Number(currentAmount) - Number(expectedAmountSeats) - Number(totalBaggagePrice) - Number(priorityPrice);
          fareSelected.total_price = fareSelected.total + Number(fareSelected.vyooCommission);
          farePricing.total = fareSelected.total_price;
          
          const currentPrice = (fareSelected.total_price +  expectedAmountSeats + totalBaggagePrice + priorityPrice).toFixed(2);

          this.translate
            .get([
              "flight.detail-flight.price-changed",
              "flight.detail-flight.previous-price",
              "flight.detail-flight.current-price",
              "flight.detail-flight.change-detected",
              "project.accept",
              "common.cancel",
            ])
            .subscribe((translations) => {
              swal.fire({
                icon: "warning",
                title: translations["flight.detail-flight.change-detected"],
                html: `
                  <p>${translations["flight.detail-flight.price-changed"]}</p>
                  <p><strong>${translations["flight.detail-flight.previous-price"]}:</strong> ${previousPrice} ${currency}</p>
                  <p"><strong>${translations["flight.detail-flight.current-price"]}:</strong> ${currentPrice} ${currency}</p>
                `,
                showCancelButton: true,
                confirmButtonText: translations["project.accept"],
                cancelButtonText: translations["common.cancel"],
                customClass: {
                  popup: "price-change-popup",
                  confirmButton: "simple-confirm-btn",
                  cancelButton: "simple-cancel-btn",
                },
                buttonsStyling: false,
              }).then((result) => {
                if (result.isConfirmed) {
                  priceChanged = currentPrice;
                  this.doFlightBooking(confirmDto, projectData, bookingQuery, fareSelected, expectedAmountSeats, totalBaggagePrice, priorityPrice,
                  priceChanged, navigationExtras, externalUser, canUseTRM, user, selectedFlights, travelersAge, flightSelectedStateBackup, farePricing, totalPrice,
                  canUseResident, selectedBaggages, selectedPriority, extraInfoForm, fareName, residentOptions, userUuid, isPackageRate, callbacks);
                } else {
                  this.utilsService.loadingFlights = false;
                  callbacks?.onFail?.('CANCELLED_PRICE_CHANGE');
                }
              });
            });
          } else if (
            err.status === 409 && (
              err.error?.error === "AVAIL_OUT" ||
              err.error?.error === "avail_out" ||
              err.error?.id    === "AVAIL_OUT"
            )
          ) {
            this.utilsService.loadingFlights = false;

            this.translate
              .get([
                "flight.detail-flight.avail-out-title",
                "flight.detail-flight.avail-out-text",
                "project.accept"
              ])
              .pipe(take(1))
              .subscribe((t) => {
                const title =
                  t["flight.detail-flight.avail-out-title"] ||
                  "Sin disponibilidad para este vuelo";
                const text =
                  t["flight.detail-flight.avail-out-text"] ||
                  "No hay disponibilidad para este vuelo con las opciones seleccionadas. Por favor, elige otra opción.";
                const accept =
                  t["project.accept"] || "Aceptar";

                swal
                  .fire({
                    icon: "warning",
                    title,
                    text,
                    showCancelButton: false,
                    confirmButtonText: accept,
                    customClass: {
                      popup: "price-change-popup",
                      confirmButton: "simple-confirm-btn",
                    },
                    buttonsStyling: false,
                  })
                  .then((result) => {
                    if (result.isConfirmed) {
                      callbacks?.onAvailOut?.();
                      return;
                    }
                  });
              });
          } else if (err.status === 817) {
            callbacks?.onFail?.('OUTDATED_817', err);
            if(projectData && projectData.selectedProject && projectData.selectedProject.uuid !== "none"){
              if (err.error.letItGoBooks && Array.isArray(err.error.letItGoBooks)) {
                err.error.letItGoBooks.forEach((letItGoBook) => {
                  if (letItGoBook && letItGoBook.uuid) {
                    this.addToTravel(letItGoBook, projectData);
                  }
                });
              }
            }
            this.cleanData();
            navigationExtras.state["isOutdated"] = true;
            this.utilsService.loadingFlights = false;
            if (!isFlightPack) {
              this.redirectToInit(externalUser, canUseTRM, navigationExtras);
            }
            return;
        }else if (err.status === 403 && err.error && err.error.message === "Su empresa no dispone de una tarjeta válida, contacte con su administrador") {
          callbacks?.onFail?.('CARD_INVALID', err);
          this.utilsService.loadingFlights = false;
          this.translate.get(["common.card-permission-error"])
            .pipe(take(1))
            .subscribe((value) =>
              this.ngxToastrService.typeInfo(null, value["common.card-permission-error"])
            );
        } else {
          callbacks?.onLetItGo?.(err);
          if(projectData && projectData.selectedProject && projectData.selectedProject.uuid !== "none"){
            if (err.error.letItGoBooks && Array.isArray(err.error.letItGoBooks)) {
              err.error.letItGoBooks.forEach((letItGoBook) => {
                if (letItGoBook && letItGoBook.uuid) {
                  this.addToTravel(letItGoBook, projectData);
                }
              });
            }
          }
          this.cleanData();
          this.utilsService.loadingFlights = false;
          swal.close();
          if (!isAccommodationPack) {
            this.translate
              .get(["common.congrat", "common.operations"])
              .subscribe((resultOperations) => {
                swal
                  .fire({
                    position: "center",
                    imageUrl: "../../../../../assets/img/svg/check-circle.svg",
                    title: resultOperations["common.congrat"],
                    text: resultOperations["common.operations"],
                    showConfirmButton: false,
                    timer: this.utilsService.letItGoTimer,
                    timerProgressBar: true,
                  })
                  .then(() => {
                    if (!isFlightPack) {
                      this.redirectToInit(externalUser, canUseTRM, navigationExtras);
                    }
                  });
              });
          }
          const unavail = err.status === 400 && err.error && err?.error?.message?.startsWith('Flight-Wrapper.flightOffersSeach: "ErrorRS: KO - AVAIL_OUT') ? true : false
          const shouldSkipRedirect = isFlightPack || isAccommodationPack;
          this.letItGo(user, selectedFlights, travelersAge, flightSelectedStateBackup, farePricing, totalPrice,
            canUseResident, selectedBaggages, selectedPriority, extraInfoForm, fareName, fareSelected, residentOptions, projectData,
            userUuid, externalUser, canUseTRM, navigationExtras,null, null, null, bookingQuery.ancillaries, null, unavail, null, null, null, shouldSkipRedirect);
        }
      }
    );
  }

  goBack(didSearch) {
    if (didSearch == true) {
      this.router.navigate(['/flight'], { queryParams: { didSearch: true } });
    } else {
      history.back();
    }
  }

  cleanData() {
    this.stateService.flightSearchForm = null;
    this.stateService.transportRequest = null;
    this.stateService.flightSelectedState = null;
    this.stateService.didFirstSearch = false;
    this.stateService.selectedUsers = [];
  }

  requestAuthorization(
    authorization,
    externalUser,
    canUseTRM,
    navigationExtras,
    opts?: {
      skipRedirect?: boolean;
      onSuccess?: () => void;
      onFail?: (err: any) => void;
    }
  ) {
    this.bookedService.requestAuthorization(authorization).subscribe(
      () => {
        this.utilsService.loadingFlights = false;

        opts?.onSuccess?.();
        if (opts?.skipRedirect) return;

        this.translate.get(["common.congrat", "authorization.flight"]).subscribe((response) => {
          swal.fire({
            position: "center",
            icon: "success",
            title: response["common.congrat"],
            text: response["authorization.flight"],
            showConfirmButton: false,
            timer: 5000,
          }).then(() => {
            this.authService.totalAuthorizations++;
            this.redirectToInit(externalUser, canUseTRM, navigationExtras);
          });
        });
      },
      (err) => {
        this.utilsService.loadingFlights = false;

        opts?.onFail?.(err);
        if (opts?.skipRedirect) return;

        swal.close();
        if (err.status === 403 && err.error?.message === "This action is not allowed in this plan") {
          this.translate.get("authorization.not-allowed-plan").subscribe((m) => this.ngxToastrService.typeInfo('', m));
        } else {
          this.translate.get("authorization.error").subscribe((resp) => this.ngxToastrService.typeInfo(resp, err.error?.message));
        }
      }
    );
  }

  postFlightAvail(flightAvailReq: FlightAvailReq) {
    const token = this.tokenService.getToken();
    const reqHeaders = {
      Authorization: `JWT ${token}`,
    };
    return this.http.post<FlightAvailRes>(
      `${environment.baseUrlApi}/grandpa/avail`,
      flightAvailReq,
      { headers: reqHeaders }
    );
  }

  getFlightPricing(flightPricingReq: any): Observable<PricingRes> {
    const token = this.tokenService.getToken();
    const reqHeaders = {
      Authorization: `JWT ${token}`,
    };
    let params = new HttpParams()
      .set("token", flightPricingReq.token)
      .set("solutionID0", flightPricingReq.solutionID0)
      .set("journey00", flightPricingReq.journey00)
      .set("userUuid", flightPricingReq.userUuid)
      .set("passengers", flightPricingReq.passengers);
    if (flightPricingReq.resident && flightPricingReq.resident !== 'none') {
      params = params.set("resident", flightPricingReq.resident);
    }
    if (flightPricingReq.journey01 != null) {
      params = params.set("journey01", flightPricingReq.journey01);
    }
    return this.http.get<PricingRes>(
      `${environment.baseUrlApi}/grandpa/pricing`,
      {
        headers: reqHeaders,
        params,
      }
    );
  }

  getFlightPolicy(flightPolicyReq: any): Observable<any> {
    const token = this.tokenService.getToken();
    const reqHeaders = {
      Authorization: `JWT ${token}`,
    };
    let params = new HttpParams()
      .set("token", flightPolicyReq.token)
      .set("solutionID0", flightPolicyReq.solutionID0)
      .set("journey00", flightPolicyReq.journey00)
      .set("userUuid", flightPolicyReq.userUuid);
    if (flightPolicyReq.journey01 != null) {
      params = params.set("journey01", flightPolicyReq.journey01);
    }
    return this.http.get<any>(`${environment.baseUrlApi}/grandpa/policies`, {
      headers: reqHeaders,
      params,
    });
  }

  getFlightStandalonecatalogue(
    flightBaggagesReq: any, type?: any
  ): Observable<StandAloneCatalogueRes> {
    const token = this.tokenService.getToken();
    const reqHeaders = {
      Authorization: `JWT ${token}`,
    };
    let params = new HttpParams()
      .set("token", flightBaggagesReq.token)
      .set("solutionID0", flightBaggagesReq.solutionID0)
      .set("journey00", flightBaggagesReq.journey00)
      .set("userUuid", flightBaggagesReq.userUuid)
      .set("type", type);
    if (flightBaggagesReq.journey01 != null) {
      params = params.set("journey01", flightBaggagesReq.journey01);
    }
    return this.http.get<StandAloneCatalogueRes>(
      `${environment.baseUrlApi}/grandpa/standalone-catalogue`,
      { headers: reqHeaders, params }
    );
  }

  postFlightBooking(flightReq: any): Observable<any> {
    const token = this.tokenService.getToken();
    const reqHeaders = {
      Authorization: `JWT ${token}`,
    };
    return this.http.post<any>(
      `${environment.baseUrlApi}/grandpa/book`,
      flightReq,
      { headers: reqHeaders }
    );
  }

  putFlightEmit(bookingId: string, flightReq: any) {
    let params = new HttpParams();
    params = params.append("locator", flightReq.params.locator);
    params = params.append("payment", flightReq.params.payment);
    if (flightReq.params.userUuid) {
      params = params.append("userUuid", flightReq.params.userUuid);
    }
    const token = this.tokenService.getToken();
    const reqHeaders = {
      Authorization: `JWT ${token}`,
    };
    return this.http.put<any>(
      `${environment.baseUrlApi}/grandpa/emit/${bookingId}`,
      flightReq.body,
      { headers: reqHeaders, params }
    );
  }

  putFlightCancel(
    lleegoReservationUuid: string,
    bookedUuid: string,
    userUuid: string,
    locator: string,
    companyUuid?: string
  ) {
    const token = this.tokenService.getToken();
    // const reqHeaders = {
    //   Authorization: `JWT ${token}`,
    //   "Content-Type": "application/x-www-form-urlencoded",
    // };
    // let formData = new FormData();
    // formData.append("userUuid", userUuid);
    // return this.http.put<any>(
    //   `${environment.baseUrlApi}/grandpa/cancel/${lleegoReservationUuid}?locator=${locator}`,
    //   formData,
    //   { headers: reqHeaders }
    // );

    const myHeaders = new Headers();
    myHeaders.append("Authorization", `JWT ${token}`);
    myHeaders.append("Content-Type", "application/x-www-form-urlencoded");

    const urlencoded = new URLSearchParams();
    urlencoded.append("uuid", bookedUuid);
    urlencoded.append("userUuid", userUuid);
    urlencoded.append("companyUuid", companyUuid);

    const requestOptions = {
      method: "PUT",
      headers: myHeaders,
      body: urlencoded,
      redirect: "follow" as RequestRedirect,
    };

    const promise = fetch(
      `${environment.baseUrlApi}/grandpa/cancel/${lleegoReservationUuid}?locator=${locator}`,
      requestOptions
    );
    return from(promise);
  }

  seatsAvailability(
    lleegoBookingID: string,
    locator?: string,
    userUuid?: string
  ): Observable<SeatsAvailabilityRes> {
    const token = this.tokenService.getToken();
    const reqHeaders = {
      Authorization: `JWT ${token}`,
    };
    let params = new HttpParams();
    params = params.append("userUuid", userUuid);
    return this.http.get<SeatsAvailabilityRes>(
      `${environment.baseUrlApi}/grandpa/seats/availability?bookingID=${lleegoBookingID}&locator=${locator}`,
      { headers: reqHeaders, params }
    );
  }

  putSeatAvailabilityConfirm(
    bookingID: string,
    confirmSeatVyoo: any,
    userUuid?: string
  ) {
    const token = this.tokenService.getToken();
    const reqHeaders = {
      Authorization: `JWT ${token}`,
    };
    let params = new HttpParams();
    params = params.append("userUuid", userUuid);
    return this.http.put<any>(
      `${environment.baseUrlApi}/grandpa/seats/confirm?bookingID=${bookingID}`,
      confirmSeatVyoo,
      { headers: reqHeaders, params }
    );
  }

  getMultifamily(multifamilyReq: any) {
    const token = this.tokenService.getToken();
    const reqHeaders = {
      Authorization: `JWT ${token}`,
    };
    let params = new HttpParams()
      .set("token", multifamilyReq.token)
      .set("travelID", multifamilyReq.travelID)
      .set("journey0", multifamilyReq.journey0)
      .set("userUuid", multifamilyReq.userUuid);

    if (multifamilyReq.journey1 != null) {
      params = params.set("journey1", multifamilyReq.journey1);
    }

    return this.http.get<any>(`${environment.baseUrlApi}/grandpa/multifamily`, {
      headers: reqHeaders,
      params,
    });
  }
}
